/**
 * SessionDefaultsInheritanceSummary
 *
 * Shows what values are inherited at each layer (Global → Cycle → Track → Session)
 * and what the effective value is. Use on track page, cycle page, global/workflow
 * page, and in the Create Session modal so users see what controls what and can
 * choose to accept or override.
 */

import type {
  SessionDefaultsInheritanceBreakdown,
  SessionDefaultsScope,
} from '@/lib/effectiveConfigResolver';

export type InheritanceSummaryScope = 'global' | 'cycle' | 'track' | 'session' | 'full';

const LAYER_LABELS: Record<SessionDefaultsScope, string> = {
  global: 'Workspace',
  cycle: 'Cycle',
  track: 'Track',
  session: 'Session',
};

const KEY_LABELS: Record<keyof Omit<SessionDefaultsInheritanceBreakdown, 'effective'>, string> = {
  timingMode: 'Session mode',
  entryMode: 'Data entry',
  defaultFacilitatorId: 'Default facilitator',
};

export interface SessionDefaultsInheritanceSummaryProps {
  breakdown: SessionDefaultsInheritanceBreakdown;
  /** Which scope this section is for; determines which columns to show and highlight */
  scope: InheritanceSummaryScope;
  /** Optional compact mode (e.g. for Create Session modal) */
  compact?: boolean;
  className?: string;
}

export function SessionDefaultsInheritanceSummary({
  breakdown,
  scope,
  compact = false,
  className = '',
}: SessionDefaultsInheritanceSummaryProps) {
  const showGlobal = scope === 'full' || scope === 'global' || scope === 'cycle' || scope === 'track' || scope === 'session';
  const showCycle = scope === 'full' || scope === 'cycle' || scope === 'track' || scope === 'session';
  const showTrack = scope === 'full' || scope === 'track' || scope === 'session';
  const showSession = scope === 'full' || scope === 'session';

  const keys: (keyof Omit<SessionDefaultsInheritanceBreakdown, 'effective'>)[] = [
    'timingMode',
    'entryMode',
    'defaultFacilitatorId',
  ];

  if (compact) {
    return (
      <div className={`rounded border border-[var(--rule)] bg-[var(--surface)] p-3 text-sm ${className}`}>
        <p className="font-medium text-[var(--text-sec)] mb-2">Inherited values (Workspace → Cycle → Track → Session)</p>
        <ul className="space-y-1 text-[var(--text-sec)]">
          {keys.map((key) => {
            const b = breakdown[key];
            const label = KEY_LABELS[key];
            return (
              <li key={key}>
                <span className="font-medium text-[var(--text-sec)]">{label}:</span>{' '}
                <span className="text-[var(--text-sec)]">
                  {b.global} → {b.cycle} → {b.track} → {b.session}
                </span>
                {' → '}
                <span className="font-medium text-[var(--text-pri)]">Effective: {b.effective}</span>
                <span className="ml-1 text-xs text-[var(--text-ter)]">(from {LAYER_LABELS[b.source]})</span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  return (
    <div className={` border border-[var(--rule)] bg-[var(--surface)]/80 p-4 ${className}`}>
      <h4 className="text-sm font-medium text-[var(--text-pri)] mb-3">What values are inherited</h4>
      <p className="text-xs text-[var(--text-sec)] mb-3">
        Order: <strong>Workspace</strong> → <strong>Cycle</strong> → <strong>Track</strong> → <strong>Session</strong>. The first level that sets a value is used; later levels can override. Cycle and track defaults are set on the cycle or track. Session mode and data entry apply when creating or running a session.
        {scope !== 'global' && (
          <span className="block mt-1.5">
            Participants and groups are configured at cycle and track, or prescribed by the workflow (Settings → Workflows).
          </span>
        )}
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs border-collapse">
          <thead>
            <tr className="border-b border-[var(--rule)]">
              <th className="text-left py-2 pr-4 font-medium text-[var(--text-sec)]">Setting</th>
              {showGlobal && (
                <th className={`text-left py-2 px-2 font-medium ${scope === 'global' ? 'text-[var(--text-ter)] bg-[var(--slate-dim)]/50' : 'text-[var(--text-sec)]'}`}>
                  {LAYER_LABELS.global}
                </th>
              )}
              {showCycle && (
                <th className={`text-left py-2 px-2 font-medium ${scope === 'cycle' ? 'text-[var(--text-ter)] bg-[var(--slate-dim)]/50' : 'text-[var(--text-sec)]'}`}>
                  Cycle
                </th>
              )}
              {showTrack && (
                <th className={`text-left py-2 px-2 font-medium ${scope === 'track' ? 'text-[var(--text-ter)] bg-[var(--slate-dim)]/50' : 'text-[var(--text-sec)]'}`}>
                  Track
                </th>
              )}
              {showSession && (
                <th className={`text-left py-2 px-2 font-medium ${scope === 'session' ? 'text-[var(--text-ter)] bg-[var(--slate-dim)]/50' : 'text-[var(--text-sec)]'}`}>
                  Session
                </th>
              )}
              <th className="text-left py-2 pl-2 font-medium text-[var(--text-pri)]">Effective (used)</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((key) => {
              const b = breakdown[key];
              return (
                <tr key={key} className="border-b border-[var(--rule)]">
                  <td className="py-2 pr-4 font-medium text-[var(--text-sec)]">{KEY_LABELS[key]}</td>
                  {showGlobal && (
                    <td className={`py-2 px-2 ${scope === 'global' ? 'bg-[var(--slate-dim)]/30' : ''} text-[var(--text-sec)]`}>
                      {b.global}
                    </td>
                  )}
                  {showCycle && (
                    <td className={`py-2 px-2 ${scope === 'cycle' ? 'bg-[var(--slate-dim)]/30' : ''} text-[var(--text-sec)]`}>
                      {b.cycle}
                    </td>
                  )}
                  {showTrack && (
                    <td className={`py-2 px-2 ${scope === 'track' ? 'bg-[var(--slate-dim)]/30' : ''} text-[var(--text-sec)]`}>
                      {b.track}
                    </td>
                  )}
                  {showSession && (
                    <td className={`py-2 px-2 ${scope === 'session' ? 'bg-[var(--slate-dim)]/30' : ''} text-[var(--text-sec)]`}>
                      {b.session}
                    </td>
                  )}
                  <td className="py-2 pl-2 font-medium text-[var(--text-pri)]">
                    {b.effective}
                    <span className="ml-1 text-[var(--text-ter)] font-normal">(from {LAYER_LABELS[b.source]})</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SessionDefaultsInheritanceSummary;
